import { useTranslations } from "next-intl";
import { daysOfWeek } from "@/constants/enum";

/**
 * A custom hook that returns the days of the week, mapping the immutable English keys
 * to their localized display labels based on the current active locale.
 *
 * It relies on the `daysOfWeek` constant for the source of truth for keys, ensuring
 * compatibility with data objects (like `businessHours`) that use English keys.
 *
 * @returns {Array<{ key: string, label: string }>} An array of objects where:
 * - `key`: The English identifier (e.g., "monday", "tuesday") used for logic/lookups.
 * - `label`: The translated string (e.g., "Lundi", "Mardi") used for UI display.
 *
 * @example
 * const localizedDays = useLocalizedDays();
 *
 * // Result in 'fr-CA':
 * // [
 * //   { key: "sunday", label: "Dimanche" },
 * //   { key: "monday", label: "Lundi" },
 * //   ...
 * // ]
 */
export const useLocalizedDays = () => {
  const t = useTranslations("DaysOfWeek");

  return daysOfWeek.map((day) => ({
    key: day,
    label: t(day),
  }));
};
